import { DrillInRow, SegmentedControl } from '../../../ui';
import { isFilterableField } from '../../filters/filterModel';
import { describeGroupValue } from './GroupControls';
import { groupableColumns } from './groupModel';
import {
  describeColumnsValue,
  describeFilterValue,
  describeSortValue,
  SHEET_LABEL,
  type TableControlProps,
  type TableSheetPageId,
} from './TableSheetControls';

/**
 * THE ADJUST SHEET'S SECTIONS: which records, and the four controls that narrow, order,
 * trim and arrange them.
 *
 * IT USED TO BE TWO LAYOUTS (2026-07-29, Arif on his phone) and it is one again. The
 * `toolbar` layout was the DESKTOP row - segment, filter, sort, columns, group - and P4-4
 * moved those four onto the list's header row as capsules beside the view chip and the
 * filter tokens, which is Mail's construction and the one the spec asks for
 * (`ListHeaderRow`). What is left here is the phone's shape, where the same settings are
 * drill-in rows: each carries its current value and a chevron and pushes a sub-page inside
 * the sheet (`SheetNavigator`, pages in `TableSheetControls`), because a desktop popover
 * floating over a sheet is a second modal layer.
 *
 * The rows live here because their VALUES are these props; the pages live next door
 * because they are their own screens.
 */

/**
 * ACTIVE | ARCHIVED (admin-area.md D21). The archive is reached from the object the
 * records were deleted from, with the sort and filters the user already knows, rather than
 * from a global "deleted records" screen - which nobody can navigate and which would list
 * rows across objects a caller may later not be permitted to read.
 *
 * A segmented control rather than a checkbox: these are two mutually exclusive views of one
 * list, which is what the sliding thumb is for. "Include archived" would promise a merged
 * list that neither the server nor this toolbar offers.
 */
export const RECORD_SEGMENTS = [
  { value: 'active', label: 'Active' },
  { value: 'archived', label: 'Archived' },
] as const;

export type RecordSegment = (typeof RECORD_SEGMENTS)[number]['value'];

export interface TableToolbarProps extends TableControlProps {
  readonly segment: RecordSegment;
  readonly onSegmentChange: (next: RecordSegment) => void;
  /** Push the sub-page a drill-in row stands for. */
  readonly onDrillIn?: ((page: TableSheetPageId) => void) | undefined;
}

export function TableToolbar({
  objectId,
  relations,
  columns,
  shownFieldIds,
  sort,
  filter,
  grouping,
  segment,
  onSegmentChange,
  onDrillIn,
}: TableToolbarProps) {
  const segmentControl = (
    /* It chooses WHICH records the other four then narrow, order, trim and arrange, so it
       gets the first section in the sheet. */
    <SegmentedControl
      size="md"
      className="w-full"
      value={segment}
      onValueChange={onSegmentChange}
      ariaLabel="Records"
      segments={RECORD_SEGMENTS.map((entry) => ({ value: entry.value, label: entry.label }))}
    />
  );

  /**
   * The filter row disappears when nothing on the object can be filtered, which is the same
   * rule the header row's "Add Filter" capsule follows: a control that opens an editor with
   * no fields in it is a dead end.
   */
  const filterable =
    columns.some((column) => isFilterableField(column.field)) ||
    (relations.anchorsOf(objectId) ?? []).length > 0;
  // Same rule, one setting along: a row that opens a picker with no fields in it is a
  // dead end, and an object with nothing groupable has exactly that picker.
  const groupable = groupableColumns(columns).length > 0;
  const drill = (page: TableSheetPageId) => () => onDrillIn?.(page);

  return (
    <div className="space-y-5">
      <section className="flex flex-col gap-2">
        <h3 className={SHEET_LABEL}>Records</h3>
        {segmentControl}
      </section>

      {/* One section for the three that narrow, order and trim the same list. Three
            labels over three single rows would be a paragraph of headings. */}
      <section className="flex flex-col gap-1">
        <h3 className={SHEET_LABEL}>Filter and sort</h3>
        {filterable && (
          <DrillInRow
            icon="filter"
            label="Filter"
            value={describeFilterValue(filter, columns, relations)}
            onClick={drill('filter')}
          />
        )}
        <DrillInRow
          icon="sort"
          label="Sort"
          value={describeSortValue(sort, columns)}
          onClick={drill('sort')}
        />
        <DrillInRow
          icon="eye"
          label="Columns"
          value={describeColumnsValue(shownFieldIds, columns)}
          onClick={drill('columns')}
        />
        {/*
            GROUPING IS THE FOURTH ROW IN THE SAME SECTION (group-by-aggregation.md D22).
            It belongs with the three that already shape one list rather than in a section
            of its own: grouping does not change WHICH records are shown, it changes how
            they are arranged, which is the same job sorting does one row up.
          */}
        {groupable && (
          <DrillInRow
            icon="board"
            label="Group"
            value={describeGroupValue(grouping, columns)}
            onClick={drill('group')}
          />
        )}
      </section>
    </div>
  );
}
